import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ElementRef, AfterViewInit, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { forkJoin, Subscription } from 'rxjs';
import {
  faArrowLeft,
  faSpinner,
  faExclamationTriangle,
  faChevronRight,
  faChevronDown,
  faChevronLeft,
  faCheckCircle,
  faGaugeHigh,
  faList,
  faChartColumn,
} from '@fortawesome/free-solid-svg-icons';
import Chart from 'chart.js/auto';
import { NgSelectModule } from '@ng-select/ng-select';
import { AgGridModule } from 'ag-grid-angular';
import { ModuleRegistry } from 'ag-grid-community';
import { ClientSideRowModelModule } from 'ag-grid-community';
import { ColumnAutoSizeModule } from 'ag-grid-community';
import { ValidationModule } from 'ag-grid-community';
import { TextFilterModule } from 'ag-grid-community';
import { NumberFilterModule } from 'ag-grid-community';
import { DateFilterModule } from 'ag-grid-community';
import { CustomFilterModule } from 'ag-grid-community';
import { RowAutoHeightModule } from 'ag-grid-community';
import { RowStyleModule } from 'ag-grid-community';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  ValidationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  RowAutoHeightModule,
  RowStyleModule,
]);

import { ResultsService } from '../../services/results.service';
import { TenantService } from '../../services/tenant.service';
import { ThemeService } from '../../services/theme.service';
import { TruncateMiddlePipe } from '../../pipes/truncate-middle.pipe';
import { CompareContext, CompareData, PathSegment, RESULT_COLORS } from './result-compare.types';
import { formatValue, getSimilarityColor, getArchiveAccuracy, isClassificationMode } from './result-compare.stats';
import { SimilarityHeaderComponent } from './result-compare.header';
import {
  renderArchiveAccuracyChart,
  renderClassAgreementChart,
  renderMetricsChart,
  renderProblematicFieldsChart,
  renderQualityDistributionChart,
  renderTokensChart,
} from './result-compare.charts';

@Component({
  selector: 'app-result-compare',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, FontAwesomeModule, NgSelectModule, AgGridModule, TruncateMiddlePipe],
  templateUrl: './result-compare.component.html',
  styleUrls: ['./result-compare.component.scss'],
})
export class ResultCompareComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('tokensChart') tokensChartCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('metricsChart') metricsChartCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('archiveAccuracyChart') archiveAccuracyChartCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('qualityDistributionChart') qualityDistributionChartCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('problematicFieldsChart') problematicFieldsChartCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('classAgreementChart') classAgreementChartCanvases!: QueryList<ElementRef<HTMLCanvasElement>>;

  interactionId = '';
  resultIds: string[] = [];
  referenceResultId = '';

  loading = true;
  error = '';

  dataList: CompareData[] = [];

  selectedArchive = '';
  allArchives: string[] = [];

  fieldFilter = '';
  selectedFieldFilters: string[] = [];
  minSimilarity = '';

  gridRowData: any[] = [];
  gridColDefs: any[] = [];
  private gridApi: any;

  private expandedKeys = new Set<string>();

  private charts: Chart[] = [];
  private viewInitialized = false;

  icons = {
    faArrowLeft, faSpinner, faExclamationTriangle,
    faChevronRight, faChevronDown, faChevronLeft,
    faCheckCircle, faGaugeHigh, faList, faChartColumn,
  };

  activeTab: 'list' | 'stats' = 'list';

  isDarkTheme = false;
  private themeSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private resultsService: ResultsService,
    private tenantService: TenantService,
    private translate: TranslateService,
    private ngZone: NgZone,
    private themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    this.isDarkTheme = this.themeService.currentTheme === 'dark';
    this.themeSub = this.themeService.theme$.subscribe(theme => {
      this.isDarkTheme = theme === 'dark';
    });

    this.interactionId = this.route.snapshot.paramMap.get('interactionId') || '';
    this.resultIds = this.route.snapshot.queryParamMap.getAll('r');

    if (this.resultIds.length < 2) {
      this.error = 'COMPARE.ERROR.MISSING_IDS';
      this.loading = false;
      return;
    }

    this.referenceResultId = this.resultIds[0];
    this.loadData();
  }

  private loadData(): void {
    const requests: Record<string, any> = {};

    this.resultIds.forEach(id => {
      const refId = id === this.referenceResultId ? undefined : this.referenceResultId;
      requests[id] = this.resultsService.getCompareData(this.interactionId, id, refId);
    });

    forkJoin(requests).subscribe({
      next: (responses: Record<string, any>) => {
        this.dataList = this.resultIds.map(id => responses[id].compare_data);
        this.computeAllArchives();
        this.prepareGridData();
        this.loading = false;
        if (this.viewInitialized) {
          this.renderCharts();
        }
      },
      error: () => {
        this.error = 'COMPARE.ERROR.LOAD_FAILED';
        this.loading = false;
      },
    });
  }

  setReferenceResult(resultId: string): void {
    if (this.referenceResultId === resultId) return;
    this.referenceResultId = resultId;
    this.resultIds = [resultId, ...this.resultIds.filter(id => id !== resultId)];
    this.loading = true;
    this.expandedKeys.clear();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (!this.loading && this.dataList.length > 0) {
      this.renderCharts();
    }
  }

  ngOnDestroy(): void {
    this.charts.forEach(c => c.destroy());
    this.themeSub?.unsubscribe();
  }

  labelFor(index: number): string {
    const d = this.dataList[index];
    return d
      ? this.translate.instant('INTERACTIONS.RESULTS.RESULT_NAME', { number: d.result_number })
      : '';
  }

  colorFor(index: number) {
    return RESULT_COLORS[index % RESULT_COLORS.length];
  }

  onArchiveChange(archive: string): void {
    this.selectedArchive = archive;
    this.expandedKeys.clear();
    this.prepareGridData();
  }

  getRowClass = (params: any): string => {
    return params.data?.isSubRow ? 'sub-row' : '';
  };

  getRowId = (params: any): string => params.data.id;

  toggleExpand(key: string): void {
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key);
    } else {
      this.expandedKeys.add(key);
    }
    this.prepareGridData();
  }

  private isExpandable(key: string): boolean {
    return this.dataList.some(d => {
      const v = d.archives[this.selectedArchive]?.kv_data?.[key];
      return v !== null && typeof v === 'object';
    });
  }

  private subFieldPaths(key: string): PathSegment[][] {
    const seen = new Map<string, PathSegment[]>();
    this.dataList.forEach(d => {
      const v = d.archives[this.selectedArchive]?.kv_data?.[key];
      if (v !== null && typeof v === 'object') {
        this.flattenValue(v).forEach(entry => {
          const label = this.pathLabel(entry.path);
          if (!seen.has(label)) seen.set(label, entry.path);
        });
      }
    });
    return Array.from(seen.values());
  }

  private pathLabel(path: PathSegment[]): string {
    return path
      .map((seg, i) => (typeof seg === 'number' ? `[${seg}]` : i === 0 ? seg : `.${seg}`))
      .join('');
  }

  private flattenValue(val: any, path: PathSegment[] = []): { path: PathSegment[]; value: any }[] {
    if (val === null || val === undefined) {
      return [{ path, value: val }];
    }
    if (Array.isArray(val)) {
      if (val.length === 0) return [{ path, value: val }];
      return val.flatMap((item, i) => this.flattenValue(item, [...path, i]));
    }
    if (typeof val === 'object') {
      const keys = Object.keys(val);
      if (keys.length === 0) return [{ path, value: val }];
      return keys.flatMap(k => this.flattenValue(val[k], [...path, k]));
    }
    return [{ path, value: val }];
  }

  private getByPath(val: any, path: PathSegment[]): any {
    let cur = val;
    for (const seg of path) {
      if (cur === null || cur === undefined) return undefined;
      cur = cur[seg as keyof typeof cur];
    }
    return cur;
  }

  private prepareGridData(): void {
    if (!this.selectedArchive || this.dataList.length === 0) {
      this.gridRowData = [];
      this.gridColDefs = [];
      return;
    }

    this.gridColDefs = [
      {
        field: 'fieldName',
        headerName: 'Campo',
        width: 220,
        minWidth: 220,
        maxWidth: 220,
        pinned: 'left',
        flex: 0,
        suppressSizeToFit: true,
        resizable: false,
        wrapText: true,
        autoHeight: true,
        cellRenderer: (params: any) => {
          const row = params.data;
          const el = document.createElement('div');
          el.style.padding = '8px 12px';
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.gap = '6px';

          if (row.isSubRow) {
            el.style.paddingLeft = '28px';
            el.style.color = 'var(--text-secondary)';
            el.style.fontSize = '0.85rem';
            const span = document.createElement('span');
            span.textContent = row.fieldName;
            el.appendChild(span);
            return el;
          }

          if (row.expandable) {
            el.style.cursor = 'pointer';
            const icon = document.createElement('span');
            icon.textContent = row.expanded ? '▼' : '▶';
            icon.style.width = '10px';
            icon.style.flexShrink = '0';
            icon.style.fontSize = '0.65rem';
            icon.style.color = 'var(--text-secondary)';
            el.appendChild(icon);
            const span = document.createElement('span');
            span.textContent = row.fieldName;
            el.appendChild(span);
            el.addEventListener('click', () => this.ngZone.run(() => this.toggleExpand(row.fieldName)));
            return el;
          }

          const span = document.createElement('span');
          span.textContent = row.fieldName;
          el.appendChild(span);
          return el;
        }
      }
    ];

    const ctx = this.buildChartContext();

    this.resultIds.forEach((_, idx) => {
      const avgSimilarity = this.resultIds[idx] === this.referenceResultId
        ? null
        : getArchiveAccuracy(ctx, this.selectedArchive, idx);

      this.gridColDefs.push({
        field: `result_${idx}`,
        headerName: this.labelFor(idx),
        headerComponent: SimilarityHeaderComponent,
        headerComponentParams: { label: this.labelFor(idx), avg: avgSimilarity },
        flex: 1,
        minWidth: 220,
        resizable: false,
        wrapText: true,
        autoHeight: true,
        cellDataType: false,
        cellRenderer: (params: any) => {
          const cellData = params.value;
          if (!cellData) {
            const el = document.createElement('div');
            el.style.padding = '8px 12px';
            el.textContent = '—';
            return el;
          }

          const value = cellData.value || '—';

          if (cellData.isClassification) {
            const el = document.createElement('div');
            el.style.padding = '8px 12px';
            el.style.display = 'flex';
            el.style.alignItems = 'center';
            el.style.justifyContent = 'center';

            const score = parseFloat(value) / 100;
            const color = isNaN(score) ? 'inherit' : getSimilarityColor(score);
            const badge = document.createElement('span');
            badge.textContent = value;
            badge.style.background = isNaN(score) ? 'transparent' : color + '22';
            badge.style.color = color;
            badge.style.padding = '3px 10px';
            badge.style.borderRadius = '4px';
            badge.style.fontSize = '0.8rem';
            badge.style.fontWeight = '600';
            badge.style.whiteSpace = 'nowrap';
            el.appendChild(badge);
            return el;
          }

          const similarity = cellData.similarity;

          const el = document.createElement('div');
          el.style.padding = '8px 12px';
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.justifyContent = 'space-between';
          el.style.gap = '8px';

          const valueSpan = document.createElement('div');
          valueSpan.textContent = value;
          valueSpan.style.flex = '1';
          valueSpan.style.wordBreak = 'break-word';
          el.appendChild(valueSpan);

          if (similarity !== null) {
            const color = getSimilarityColor(similarity);
            const pct = `${(similarity * 100).toFixed(0)}%`;
            const badge = document.createElement('span');
            badge.textContent = pct;
            badge.style.background = color + '22';
            badge.style.color = color;
            badge.style.padding = '2px 4px';
            badge.style.borderRadius = '2px';
            badge.style.fontSize = '0.7rem';
            badge.style.fontWeight = '600';
            badge.style.whiteSpace = 'nowrap';
            badge.style.flexShrink = '0';
            el.appendChild(badge);
          }

          return el;
        }
      });
    });

    const rows: any[] = [];
    const allKeys = this.allKvKeys;

    const archivePrefix = this.selectedArchive;

    allKeys.forEach(key => {
      const expandable = this.isExpandable(key);
      const expanded = expandable && this.expandedKeys.has(key);
      const row: any = { id: `${archivePrefix}::${key}`, fieldName: key, expandable, expanded };

      this.dataList.forEach((data, idx) => {
        const value = data.archives[this.selectedArchive]?.kv_data?.[key];
        const similarity = this.getSimilarityScore(key, idx);
        const isClassification = !!data.archives[this.selectedArchive]?.is_classification;
        row[`result_${idx}`] = {
          value: expandable && value !== null && value !== undefined
            ? (expanded ? '' : this.translate.instant('COMPARE.KV_TABLE.FIELDS_COUNT', { count: this.subFieldPaths(key).length }))
            : formatValue(value),
          similarity,
          isClassification
        };
      });
      rows.push(row);

      if (expanded) {
        this.subFieldPaths(key).forEach(path => {
          const pathLabel = this.pathLabel(path);
          const subRow: any = { id: `${archivePrefix}::${key}::${pathLabel}`, fieldName: pathLabel, isSubRow: true };
          this.dataList.forEach((data, idx) => {
            const value = data.archives[this.selectedArchive]?.kv_data?.[key];
            subRow[`result_${idx}`] = {
              value: formatValue(this.getByPath(value, path)),
              similarity: this.getSubFieldSimilarity(key, path, idx)
            };
          });
          rows.push(subRow);
        });
      }
    });

    this.gridRowData = rows;
    this.resizeGridColumns();
  }

  private resizeGridColumns(): void {
    if (!this.gridApi) return;
    setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
  }

  get currentArchiveIndex(): number {
    return this.allArchives.indexOf(this.selectedArchive);
  }

  get hasPrevArchive(): boolean {
    return this.currentArchiveIndex > 0;
  }

  get hasNextArchive(): boolean {
    return this.currentArchiveIndex >= 0 && this.currentArchiveIndex < this.allArchives.length - 1;
  }

  goToPrevArchive(): void {
    if (this.hasPrevArchive) {
      this.onArchiveChange(this.allArchives[this.currentArchiveIndex - 1]);
    }
  }

  goToNextArchive(): void {
    if (this.hasNextArchive) {
      this.onArchiveChange(this.allArchives[this.currentArchiveIndex + 1]);
    }
  }

  get allKvKeys(): string[] {
    const keys = new Set<string>();
    this.dataList.forEach(d => {
      Object.keys(d.archives[this.selectedArchive]?.kv_data ?? {}).forEach(k => keys.add(k));
    });
    return Array.from(keys).filter(k => this.shouldShowField(k)).sort();
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.resizeGridColumns();
  }

  getSimilarityScore(key: string, resultIndex: number): number | null {
    if (this.resultIds[resultIndex] === this.referenceResultId) return null;
    const data = this.dataList[resultIndex];
    if (!data?.field_similarities) return null;
    const key_with_archive = `${this.selectedArchive}.${key}`;
    return data.field_similarities[key_with_archive] ?? null;
  }

  getSubFieldSimilarity(key: string, path: PathSegment[], resultIndex: number): number | null {
    if (this.resultIds[resultIndex] === this.referenceResultId) return null;
    const data = this.dataList[resultIndex];
    if (!data?.field_similarities) return null;
    const subKey = `${this.selectedArchive}.${key}::${this.pathLabel(path)}`;
    return data.field_similarities[subKey] ?? null;
  }

  private shouldShowField(key: string): boolean {
    if (this.selectedFieldFilters.length && !this.selectedFieldFilters.includes(key)) {
      return false;
    }

    if (this.fieldFilter) {
      const filter = this.fieldFilter.toLowerCase();
      const matchesKey = key.toLowerCase().includes(filter);
      const matchesValue = this.dataList.some(data => {
        const value = data.archives[this.selectedArchive]?.kv_data?.[key];
        return formatValue(value).toLowerCase().includes(filter);
      });
      if (!matchesKey && !matchesValue) return false;
    }

    if (this.minSimilarity) {
      const threshold = parseFloat(this.minSimilarity);
      let hasMatch = false;
      for (let i = 1; i < this.dataList.length; i++) {
        const score = this.getSimilarityScore(key, i);
        if (score !== null && score <= threshold) {
          hasMatch = true;
          break;
        }
      }
      if (!hasMatch) return false;
    }

    return true;
  }

  onFieldFilterChange(): void {
    this.prepareGridData();
  }

  onSimilarityFilterChange(): void {
    this.prepareGridData();
  }

  onFieldSelectChange(): void {
    this.prepareGridData();
  }

  formatConfidence(val: number | null | undefined): string {
    if (val === null || val === undefined) return '—';
    return (val * 100).toFixed(1) + '%';
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute(['/simulations', this.interactionId]));
  }

  setActiveTab(tab: 'list' | 'stats'): void {
    this.activeTab = tab;
    if (tab === 'stats') {
      setTimeout(() => this.charts.forEach(c => c.resize()), 0);
    } else {
      this.resizeGridColumns();
    }
  }

  private computeAllArchives(): void {
    if (this.dataList.length === 0) return;
    const all = new Set<string>();
    this.dataList.forEach(d => Object.keys(d.archives).forEach(k => all.add(k)));
    this.allArchives = [...all].sort((a, b) => a.localeCompare(b));
    if (this.allArchives.length > 0) {
      this.selectedArchive = this.allArchives[0];
    }
  }

  isArchiveMissing(index: number): boolean {
    return !this.dataList[index]?.archives[this.selectedArchive];
  }

  get resultsMissingSelectedArchive(): number[] {
    return this.dataList.map((_, i) => i).filter(i => this.isArchiveMissing(i));
  }

  get isClassificationMode(): boolean {
    return isClassificationMode(this.dataList);
  }

  private buildChartContext(): CompareContext {
    return {
      dataList: this.dataList,
      resultIds: this.resultIds,
      referenceResultId: this.referenceResultId,
      allArchives: this.allArchives,
      labelFor: (i) => this.labelFor(i),
      colorFor: (i) => this.colorFor(i),
      translate: (key) => this.translate.instant(key),
    };
  }

  private renderCharts(): void {
    if (this.dataList.length === 0) return;
    setTimeout(() => {
      this.charts.forEach(c => c.destroy());
      this.charts = [];

      const ctx = this.buildChartContext();
      const classificationMode = this.isClassificationMode;

      const rendered = [
        renderTokensChart(this.tokensChartCanvases.first?.nativeElement, ctx),
        renderMetricsChart(this.metricsChartCanvases.first?.nativeElement, ctx, classificationMode),
        ...(classificationMode
          ? [renderClassAgreementChart(this.classAgreementChartCanvases.first?.nativeElement, ctx)]
          : [
              renderArchiveAccuracyChart(this.archiveAccuracyChartCanvases.first?.nativeElement, ctx),
              renderQualityDistributionChart(this.qualityDistributionChartCanvases.first?.nativeElement, ctx),
              renderProblematicFieldsChart(this.problematicFieldsChartCanvases.first?.nativeElement, ctx),
            ]),
      ];

      this.charts = rendered.filter((c): c is Chart => c !== null);
    }, 0);
  }
}
